// Pure helpers for the per-field inline editors on the Structured Data tab
// (doc-32 phase b). Each field type maps to an editor kind; these convert a
// stored typed value to editable text and parse the edited text back —
// throwing a user-readable Error when the input can't be typed, so a hand
// edit is never silently discarded.

// Which editor a field gets. Arrays holding objects fall back to the JSON
// editor; arrays of scalars edit as one-item-per-line text.
export function editorKind(field, value) {
  switch (field.type) {
    case 'object':
      return 'json';
    case 'array': {
      const items = Array.isArray(value) ? value : [];
      return items.some((v) => typeof v === 'object' && v !== null) ? 'json' : 'lines';
    }
    case 'boolean':
    case 'enum':
      return 'select';
    case 'number':
      return 'number';
    case 'date':
      return 'date';
    default:
      return 'text';
  }
}

export function initialEditorText(field, value) {
  if (value === null || value === undefined) return '';
  switch (editorKind(field, value)) {
    case 'json':
      return JSON.stringify(value, null, 2);
    case 'lines':
      return (Array.isArray(value) ? value : [value]).map(String).join('\n');
    case 'select':
      return field.type === 'boolean' ? (value ? 'true' : 'false') : String(value);
    case 'date':
      return String(value).slice(0, 10);
    default:
      return String(value);
  }
}

// Parse edited text back to the field's typed value. Empty input clears the
// value (null = "not in the document"). `kind` is the editor that was shown —
// it depends on the ORIGINAL value for arrays, so callers pass it through.
export function parseEditorText(field, kind, text) {
  const t = String(text ?? '').trim();
  if (t === '') return null;
  switch (kind) {
    case 'number': {
      const n = Number(t.replace(/[$,\s]/g, ''));
      if (!Number.isFinite(n)) throw new Error('Enter a number');
      return n;
    }
    case 'date': {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) throw new Error('Enter a date (YYYY-MM-DD)');
      return t;
    }
    case 'select':
      return field.type === 'boolean' ? t === 'true' : t;
    case 'lines': {
      const items = t.split('\n').map((s) => s.trim()).filter(Boolean);
      return items.length ? items : null;
    }
    case 'json': {
      let parsed;
      try {
        parsed = JSON.parse(t);
      } catch (_) {
        throw new Error('Invalid JSON');
      }
      if (field.type === 'object' && (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed))) {
        throw new Error('Enter a JSON object ({ ... })');
      }
      if (field.type === 'array' && !Array.isArray(parsed)) {
        throw new Error('Enter a JSON array ([ ... ])');
      }
      return parsed;
    }
    default:
      return t;
  }
}
